/* Copyright 2016-2022
   Kabandy
   Author: Cedric Hoarau
 */

import { ApiDataModel } from "../ApiDataModel";
import { ApiRequestHandler, getApiKey } from "../ApiWeb";
import Units from "../Units/Units";
import { Common } from "../Utilities";
import { UIModal } from "./ui-modal";

type IApiBoardCell = ApiDataModel.IBoardCell;
type IApiBoardGroup = ApiDataModel.IBoardGroup;
type IApiBoardItem = ApiDataModel.IBoardItem;
type IApiBoardProperty = ApiDataModel.IBoardProperty;
type IApiBoardSection = ApiDataModel.IBoardSection;

type ISectionGroupsMap = {[sectionId: string]: IApiBoardGroup[]};
type IGroupItemsMap = {[groupId: string]: IApiBoardItem[]};
type IItemCellsMap = {[itemId: string]: IApiBoardCell[]};
type IBoardCellMeasure = ApiDataModel.IBoardCellMeasure;
type IBoardCellViewpoint = ApiDataModel.IBoardCellViewpoint;
type IBoardTextCell = ApiDataModel.IBoardCellText;

let _rootDiv: HTMLDivElement | undefined = undefined;
let _currentPropertiesHtmlListElement: HTMLUListElement | undefined = undefined;
let _currentSectionsHtmlListElement: HTMLUListElement | undefined = undefined;

let _accessToken: string = "";
let _currentProperties: IApiBoardProperty[] = [];
let _currentSections: IApiBoardSection[] = [];
let _currentSectionGroupsMap: ISectionGroupsMap = {};
let _currentGroupItemsMap: IGroupItemsMap = {};
let _currentItemCellsMap: IItemCellsMap = {};

export module UIBoardCells {
    export function Bind(accessToken: string, workspaceId: string, boardId: string): void {
        _accessToken = accessToken;
        _rootDiv = _InitializeUI(accessToken, workspaceId, boardId);
        document.body.appendChild(_rootDiv);
    }
    export function Unbind() {
        if (_rootDiv) {
            _rootDiv.remove();
        }

        _currentProperties = [];
        _currentSections = [];
        _currentSectionGroupsMap = {};
        _currentGroupItemsMap = {};
        _currentItemCellsMap = {};

        if (_currentPropertiesHtmlListElement) {
            _currentPropertiesHtmlListElement = undefined;
        }
        if (_currentSectionsHtmlListElement) {
            _currentSectionsHtmlListElement = undefined;
        }
    }

    function _InitializeUI(accessToken: string, workspaceId: string, boardId: string): HTMLDivElement {
        let rootDiv = document.createElement("div");
        rootDiv.id = "ui-board-cells-root-div";

        let titleELement = document.createElement("h2");
        titleELement.innerText = "Board Cells";
        rootDiv.appendChild(titleELement);

        // get Section > Groups > Items & Properties > Cells button
        const getButton: HTMLButtonElement = document.createElement("button");
        getButton.id = "ui-board-cells-get-sections-groups-items-properties-cells-button";
        getButton.textContent = "Get Sections > Groups > Items & Properties > Cells";
        rootDiv.appendChild(getButton);
        getButton.addEventListener("click", async () => {
            // Fetch sections & properties
            [ _currentProperties, _currentSections ] = await Promise.all([
                _FetchProperties(accessToken, workspaceId, boardId),
                _FetchSections(accessToken, workspaceId, boardId)
            ]);

            // Fetch Groups & Items
            const cGroups: IApiBoardGroup[] = await _FetchGroupsFromSections(accessToken, workspaceId, boardId, _currentSections, _currentSectionGroupsMap);
            const cItems: IApiBoardItem[] = await _FetchItemsFromGroups(accessToken, workspaceId, boardId, cGroups, _currentGroupItemsMap);

            // Fetch Cells
            await _FetchCellsFromItemsAndProperties(accessToken, workspaceId, boardId, cItems, _currentProperties, _currentItemCellsMap);

            // UI: Properties
            _currentPropertiesHtmlListElement = _RefreshUIProperties(workspaceId, boardId, _currentPropertiesHtmlListElement, _currentProperties);

            // UI: Sections > Groups > Items > Cells
            _currentSectionsHtmlListElement = _RefreshUISectionsGroupsItems(
                workspaceId, boardId,
                _currentSectionsHtmlListElement,
                _currentSections,
                _currentSectionGroupsMap,
                _currentGroupItemsMap,
                _currentItemCellsMap
            );
        });
        rootDiv.appendChild(document.createElement("br"));

        // get cells buttons
        const fetchCellsButton: HTMLButtonElement = document.createElement("button");
        fetchCellsButton.id = "ui-board-cells-fetch-button";
        fetchCellsButton.textContent = "Fetch cells";
        rootDiv.appendChild(fetchCellsButton);
        fetchCellsButton.addEventListener("click", async () => {
            const perLinesCells : IApiBoardCell[][] = await _FetchCells( accessToken, workspaceId, boardId );
            let perlinesCellsInnerHtml : string = "";
            for( let perLineCells of perLinesCells ) {
                perlinesCellsInnerHtml += "<br>";
                perlinesCellsInnerHtml += _CreateUICells( "span", perLineCells, undefined, "10%" );
            }
            UIModal.Show(perlinesCellsInnerHtml);
        });
        rootDiv.appendChild(document.createElement("br"));

        // Properties
        _currentPropertiesHtmlListElement = _CreateUIProperties( workspaceId, boardId, _currentProperties );
        rootDiv.appendChild(_currentPropertiesHtmlListElement);

        // Sections > Groups > Items
        _currentSectionsHtmlListElement = _CreateUISectionsGroupsItems( workspaceId, boardId, _currentSections, _currentSectionGroupsMap);
        rootDiv.appendChild(_currentSectionsHtmlListElement);

        return rootDiv;
    }

    //
    // --- Sections 

    function _CreateUISectionsGroupsItems(workspaceId: string, boardId: string, sections: IApiBoardSection[] = [],
        sectionGroupsMap: ISectionGroupsMap = {},
        groupItemsMap: IGroupItemsMap = {},
        itemCellsMap: IItemCellsMap = {}
    ): HTMLUListElement {
        const sectionsListElement = document.createElement("ul");

        for (let cSection of sections) {
            // Section
            let cSectionListElement = document.createElement("li");
            cSectionListElement.style.alignItems = "center";
            let cSectionDiv: HTMLDivElement = document.createElement("div");
            cSectionDiv.id = cSection.id;
            cSectionDiv.textContent = `${cSection.name}`;
            cSectionListElement.appendChild(cSectionDiv);

            // Group
            let groupsList = document.createElement("ul");
            let cGroups: IApiBoardGroup[] = sectionGroupsMap[cSection.id] || [];
            for( let cGroup of cGroups ) {
                let cGroupListElement = document.createElement("li");
                cGroupListElement.style.alignItems = "center";
                let cGroupDiv: HTMLDivElement = document.createElement("div");
                cGroupDiv.id = cGroup.id;
                cGroupDiv.textContent = `${cGroup.name}`;
                cGroupListElement.appendChild(cGroupDiv);
                groupsList.appendChild(cGroupListElement);

                // Item
                let itemsList = document.createElement("ul");
                let cItems: IApiBoardItem[] = groupItemsMap[cGroup.id] || [];
                for( let cItem of cItems ) {
                    let cItemListElement = document.createElement("li");
                    cItemListElement.style.alignItems = "center";

                    let cItemDiv: HTMLDivElement = _CreateUIItemCells( workspaceId, boardId, cItem, itemCellsMap[ cItem.id ] );
                    cItemListElement.appendChild(cItemDiv);

                    itemsList.appendChild(cItemListElement);
                }
                cGroupListElement.appendChild( itemsList );
            }
            cSectionListElement.appendChild( groupsList );

            sectionsListElement.appendChild(cSectionListElement);
        }

        return sectionsListElement;
    }
    function _CreateUIItemCells(workspaceId: string, boardId: string, item: IApiBoardItem, cells: IApiBoardCell[] = []) {
        let itemDiv: HTMLDivElement = document.createElement("div");
        itemDiv.id = item.id;
        itemDiv.style.display = "inline";

        let cItemContentNameSpan : HTMLSpanElement = document.createElement("span");
        cItemContentNameSpan.textContent = `${item.name}`;
        // Fetch row cells button
        const cFetchLineCellsButtons: HTMLButtonElement = document.createElement("button");
        cFetchLineCellsButtons.id = "ui-board-item-fetch-cells-button";
        cFetchLineCellsButtons.textContent = "Fetch line cells ";
        cFetchLineCellsButtons.addEventListener("click", async () => {
            const perRowsCells : IApiBoardCell[][] = await _FetchCells( _accessToken, workspaceId, boardId, [item.id], undefined );
            const [perRowCells] : IApiBoardCell[][] = perRowsCells;
            const perRowCellsInnerHtml : string = _CreateUICells( "span", perRowCells );
            UIModal.Show(perRowCellsInnerHtml);
        });
        cItemContentNameSpan.appendChild(cFetchLineCellsButtons);
        itemDiv.appendChild( cItemContentNameSpan );

        let cItemContentCellsSpan : HTMLSpanElement = document.createElement("span");
        cItemContentCellsSpan.style.marginLeft = "10%";
        // @ts-ignore
        cItemContentCellsSpan.innerHTML = DOMPurify.sanitize(_CreateUICells( "span", cells));
        itemDiv.appendChild( cItemContentCellsSpan );
        
        return itemDiv;
    }
    function _RefreshUISectionsGroupsItems(workspaceId: string, boardId: string, currentList?: HTMLUListElement, sections: IApiBoardSection[] = [],
        sectionGroupsMap: ISectionGroupsMap = {},
        groupItemsMap: IGroupItemsMap = {},
        itemCellsMap: IItemCellsMap = {}
    ): HTMLUListElement | undefined {
        let oNextList: HTMLUListElement | undefined = undefined;

        if (currentList) {
            const nextList = _CreateUISectionsGroupsItems(workspaceId, boardId, sections, sectionGroupsMap, groupItemsMap, itemCellsMap);

            const parentElement: HTMLElement | null = currentList.parentElement;
            if (parentElement) {
                parentElement.replaceChild(nextList, currentList);
                oNextList = nextList;
            }
        }

        return oNextList;
    }
    async function _FetchSections(accessToken: string, workspaceId: string, boardId: string): Promise<IApiBoardSection[]> {
        const sections: IApiBoardSection[] = await ApiRequestHandler.GetMeBoardSections(getApiKey(), accessToken, workspaceId, boardId );
        return sections || [];
    }

    //
    // --- Groups

    async function _FetchGroupsFromSections(accessToken: string, workspaceId: string, boardId: string, sections: IApiBoardSection[] = [], ioSectionGroupsMap: ISectionGroupsMap = {}): Promise<IApiBoardGroup[]> {
        let promises : Promise<IApiBoardGroup[]>[] = [];

        for( let section of sections ) {
            promises.push( _FetchGroups( accessToken, workspaceId, boardId, section.id, ioSectionGroupsMap ) );
        }

        const perSectionGroups: IApiBoardGroup[][] = await Promise.all(promises);
        return Common.flatArray( perSectionGroups );
    }
    async function _FetchGroups(accessToken: string, workspaceId: string, boardId: string, sectionId: string, ioSectionGroupsMap: ISectionGroupsMap = {}): Promise<IApiBoardGroup[]>  {
        const groups: IApiBoardGroup[] = await ApiRequestHandler.GetMeBoardSectionGroups(getApiKey(), accessToken, workspaceId, boardId, sectionId ) || [];
        ioSectionGroupsMap[ sectionId ] = groups;
        return groups;
    }

    //
    // --- Items

    async function _FetchItemsFromGroups(accessToken: string, workspaceId: string, boardId: string, groups: IApiBoardGroup[] = [], ioGroupItemsMap: IGroupItemsMap = {} ): Promise<IApiBoardItem[]> {
        let promises: Promise<IApiBoardItem[]>[] = [];

        for( let group of groups ) {
            promises.push( _FetchItems( accessToken, workspaceId, boardId, group.sectionId, group.id, ioGroupItemsMap ) );
        }

        const perGroupItems: IApiBoardItem[][] = await Promise.all(promises);
        return Common.flatArray( perGroupItems );
    }
    async function _FetchItems(accessToken: string, workspaceId: string, boardId: string, sectionId: string, groupId: string, ioGroupItemsMap: IGroupItemsMap = {}): Promise<IApiBoardItem[]>  {
        const items: IApiBoardItem[] = await ApiRequestHandler.GetMeBoardSectionGroupItems(getApiKey(), accessToken, workspaceId, boardId, sectionId, groupId ) || [];
        ioGroupItemsMap[ groupId ] = items;
        return items;
    }

    //
    // --- Properties

    function _CreateUIProperties(workspaceId: string, boardId: string, properties: IApiBoardProperty[] = []): HTMLUListElement {
        const propertiesListElement = document.createElement("ul");
        propertiesListElement.style.display = "flex";
        propertiesListElement.style.alignItems = "center";
        propertiesListElement.style.marginLeft = "20%";
        propertiesListElement.style.listStyleType = "none";

        for (let cProperty of properties) {
            let cPropertyListElement = document.createElement("li");
            cPropertyListElement.style.alignItems = "center";
            cPropertyListElement.style.marginLeft = "5%";
            cPropertyListElement.style.marginRight = "5%";
            let cPropertyDiv: HTMLDivElement = document.createElement("div");
            cPropertyDiv.id = cProperty.id;
            cPropertyDiv.textContent = `${cProperty.name} (${cProperty.type})`;
            // cPropertyDiv.textContent = `property ${cProperty.id} | ${cProperty.name} | type: ${cProperty.type} | created at: ${cProperty.created_at} | updated at: ${cProperty.updated_at}`;
            
            // Fetch column cells button
            const cFetchColCellsButtons: HTMLButtonElement = document.createElement("button");
            cFetchColCellsButtons.id = "ui-board-property-fetch-cells-button";
            cFetchColCellsButtons.textContent = "Fetch column cells ";
            cFetchColCellsButtons.addEventListener("click", async () => {
                const perColsCells : IApiBoardCell[][] = await _FetchCells( _accessToken, workspaceId, boardId, undefined, [cProperty.id] );
                const [perColCells] : IApiBoardCell[][] = perColsCells;
                const perColCellsInnerHtml : string = _CreateUICells( "div", perColCells );
                UIModal.Show(perColCellsInnerHtml);
            });
            cPropertyDiv.appendChild(cFetchColCellsButtons);


            cPropertyListElement.appendChild(cPropertyDiv);
            propertiesListElement.appendChild(cPropertyListElement);
        }

        return propertiesListElement;
    }
    function _RefreshUIProperties(workspaceId: string, boardId: string, currentList?: HTMLUListElement, properties: IApiBoardProperty[] = []): HTMLUListElement | undefined {
        let oNextList: HTMLUListElement | undefined = undefined;

        if (currentList) {
            const nextList = _CreateUIProperties(workspaceId, boardId, properties);

            const parentElement: HTMLElement | null = currentList.parentElement;
            if (parentElement) {
                parentElement.replaceChild(nextList, currentList);
                oNextList = nextList;
            }
        }

        return oNextList;
    }
    async function _FetchProperties(accessToken: string, workspaceId: string, boardId: string): Promise<IApiBoardProperty[]> {
        const properties: IApiBoardProperty[] = await ApiRequestHandler.GetMeBoardProperties(getApiKey(), accessToken, workspaceId, boardId );
        return properties || [];
    }

    //
    // --- Cells

    function _CreateUICells( uiContainerStr: string, cells: IApiBoardCell[] = [], padLeft?: string, padRight?: string ) : string {
        let oHtmlStr: string = "";

        for( let cell of cells ) {
            oHtmlStr += _CreateUICell(uiContainerStr, cell, padLeft, padRight );
        }

        return oHtmlStr;
    }
    function _CreateUICell(uiContainerStr: string, cell: IApiBoardCell, padLeft: string = "2%", padRight : string = "2%") {
        let innerHtml = `<${uiContainerStr} style=padding-left:${padLeft};padding-right:${padRight}>cell ${cell.id}</${uiContainerStr}>`;

        const cellType : ApiDataModel.EBoardCellType = cell.type;

        if( cellType === ApiDataModel.EBoardCellType.Measure ) {
            const measureCell : IBoardCellMeasure = <IBoardCellMeasure>cell;
            let measureCellUIContent: string = "";
            if( measureCell.value != undefined ) {
                const measureCellUnitFormatted = Units.getUnitsSymbol(measureCell.unit)
                measureCellUIContent = `<${uiContainerStr} style=padding-left:${padLeft};padding-right:${padRight}>measure ${measureCell.value} ${measureCellUnitFormatted}</${uiContainerStr}>`
            } else {
                measureCellUIContent = `<${uiContainerStr} style=padding-left:${padLeft};padding-right:${padRight}>n/a</${uiContainerStr}>`;
            }
            innerHtml = measureCellUIContent;
        } else if( cellType === ApiDataModel.EBoardCellType.Viewpoint ) {
            const viewpointCell : IBoardCellViewpoint = <IBoardCellViewpoint>cell;
            let viewpointCellUIContent: string = "";
            if( viewpointCell.viewpointId != undefined ) {
                viewpointCellUIContent = `<${uiContainerStr} style=padding-left:${padLeft};padding-right:${padRight}>viewpoint ${viewpointCell.viewpointId}</${uiContainerStr}>`
            } else {
                viewpointCellUIContent = `<${uiContainerStr} style=padding-left:${padLeft};padding-right:${padRight}>n/a</${uiContainerStr}>`;
            }
            innerHtml = viewpointCellUIContent;
        } else if( cellType === ApiDataModel.EBoardCellType.Text ) {
            const textCell : IBoardTextCell = <IBoardTextCell>cell;
            if( textCell.value !== "" ) {
                innerHtml = `<${uiContainerStr} style=padding-left:${padLeft};padding-right:${padRight}>${textCell.value}</${uiContainerStr}>`;
            } else {
                innerHtml = `<${uiContainerStr} style=padding-left:${padLeft};padding-right:${padRight}>n/a</${uiContainerStr}>`;
            }
        }

        return innerHtml;
    }
    async function _FetchCellsFromItemsAndProperties(accessToken: string, workspaceId: string, boardId: string, items: IApiBoardItem[] = [], properties: IApiBoardProperty[] = [], ioItemCellsMap: IItemCellsMap = {}): Promise<IApiBoardCell[]> {
        const itemIds: string[] = items.map( item => item.id );
        const propertyIds: string[] = properties.map( property => property.id );
        const perItemCells: IApiBoardCell[][] = await _FetchCells( accessToken, workspaceId, boardId, itemIds, propertyIds );

        const itemsCount: number = itemIds.length;
        for( let i: number = 0; i < itemsCount; ++i ) {
            ioItemCellsMap[ itemIds[i] ] = perItemCells[ i ];
        }

        return Common.flatArray( perItemCells );
    }
    async function _FetchCells(accessToken: string, workspaceId: string, boardId: string, itemIds?: string[], propertyIds?: string[]): Promise<IApiBoardCell[][]> {
        const cells: IApiBoardCell[][] = await ApiRequestHandler.GetMeBoardCells(getApiKey(), accessToken, workspaceId, boardId, itemIds, propertyIds ) || [];
        return cells;
    }
}
   